/**
 * Gestion des factures fournisseurs Dolibarr.
 *
 * @author jlg
 * 8 août 2026
 */

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  HostListener
} from '@angular/core';

import {
  MatDialog,
  MatDialogConfig
} from '@angular/material';

import { DateAdapter } from '@angular/material/core';
import {
  DomSanitizer,
  SafeResourceUrl
} from '@angular/platform-browser';

import {
  BehaviorSubject,
  Subject,
  Subscription
} from 'rxjs';

import {
  takeUntil
} from 'rxjs/operators';

import {
  GridOptions
} from 'ag-grid-community';

import { TranslateService } from '../../../../../../services/translate.service';
import { UserService } from '../../../../../../services/user.service';
import { GridTools } from '../../../../../common/tools/gridTools';
import { ModalCommonComponent } from '../../../../../common/components/modal-common/modal-common.component';
import { GlobalEventsManagerService } from '../../../../../common/services/global-events-manager.service';
import { NotificationsService } from '../../../../../common/services/notifications.service';
import { ToastrService } from 'ngx-toastr';

import {
  SupplierInvoiceModel,
  SupplierInvoiceSearchModel,
  SupplierModel,
  InvoiceTrackingEventModel,
  InvoiceStatusDefinition,
  INVOICE_STATUSES
} from '../../../../models/wks-suppliersInvoices.model';

import {
  SupplierInvoicesService
} from '../../../../services/wks-suppliers-invoices.service';

import { UserModel } from '../../../../../../models/user.model';

import { HttpResponse } from '@angular/common/http';

import {
  SupplierInvoiceChangeStatusComponent
} from '../supplier-invoice-change-statut/supplier-invoice-change-statut.component';
import { LabelValue } from '../../../../../../models/data.model';


@Component({
  selector: 'mdi-supplier-invoices-table',
  templateUrl: './supplier-invoices-table.component.html',
  styleUrls: ['./supplier-invoices-table.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SupplierInvoicesTableComponent
  implements OnInit, OnDestroy {


  /*
   * ============================================================
   * AG GRID
   * ============================================================
   */

  @ViewChild('agGrid', { static: true })
  agGrid: GridOptions;

  gridOptions: any;

  gridApi: any;

  gridColumnApi: any;

  defaultColDef: any;

  displayedColumns: any[];

  localeText: any;


  /*
   * ============================================================
   * DONNEES
   * ============================================================
   */

  rows: SupplierInvoiceModel[] = [];

  cacheDataTemp: SupplierInvoiceModel[] = [];

  suppliers: SupplierModel[] = [];

  selected: SupplierInvoiceModel[] = [];

  selectedInvoice: SupplierInvoiceModel;


  /*
   * ============================================================
   * RECHERCHE
   * ============================================================
   */

  criteria: SupplierInvoiceSearchModel;

  trackingStatuses: LabelValue[] = [];

  invoiceTrackingEvents:
    InvoiceTrackingEventModel[] = [];


  /*
   * ============================================================
   * AFFICHAGE
   * ============================================================
   */

  loadingIndicator = false;

  private loadingSubject =
    new BehaviorSubject<boolean>(false);

  loading$ =
    this.loadingSubject.asObservable();


  /*
   * ============================================================
   * FORMATS
   * ============================================================
   */

  dateFormat: string;

  dateTimeFormat: string;
  timeFormat: string;

  userLangCur: string;


  /*
   * ============================================================
   * SUBSCRIPTIONS
   * ============================================================
   */

  private readonly onDestroy =
    new Subject<void>();

  notificationsSubscription:
    Subscription;
  /*
   * ============================================================
   * Affichage Pdf
   * ============================================================
   */
  pdfPopupVisible = false;
  pdfUrl: SafeResourceUrl | null = null;

  private pdfResizing = false;
  waitingPrint = false;

  /*
   * ============================================================
   * TEST
   * ============================================================
   */

  hasNextPage: boolean;

  loading: boolean;

  trackingEventsLoading: boolean;

  private testRows: SupplierInvoiceModel[] = [];

  historyPopupVisible = false;

  /*
   * ============================================================
   * CONSTRUCTEUR
   * ============================================================
   */

  constructor(
    private _translate: TranslateService,
    private _userService: UserService,
    private dateAdapter: DateAdapter<Date>,

    private _supplierInvoicesService:
      SupplierInvoicesService,

    private _dialog: MatDialog,

    private _globalEventsManagerService:
      GlobalEventsManagerService,

    private _notificationsService:
      NotificationsService,

    private _toastr: ToastrService,
    private readonly sanitizer: DomSanitizer
  ) {

    this._translate.onLangChanged
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(
        () => {

          this.translateLabels();

        }
      );
  }
  
  /*
   * ============================================================
   * INIT
   * ============================================================
   */

  ngOnInit(): void {

    const user =
      this._userService.getUserLogged();

    if (user === undefined) {
      return;
    }

    this.initVariables(user);

    this.buildStatuses();

    this.criteria =
      new SupplierInvoiceSearchModel();

    this.buildList();
    this.loadSuppliers();
    this.search();
    this.subscribeAll();
    this.connectNotifications();
  }


  /*
   * ============================================================
   * INITIALISATION
   * ============================================================
   */

  initVariables(user: UserModel): void {

    this.userLangCur =
      user.userlang;

    this.dateFormat =
      this._translate.getLocalFmt(
        'dateFormat'
      );

    this.dateTimeFormat =
      this._translate.getLocalFmt(
        'dateTimeFormat'
      );
    this.timeFormat =
      this._translate.getLocalFmt(
        'timeFormat'
      );

    this.dateAdapter.setLocale(
      user.userlang
    );

  }


  /*
   * ============================================================
   * STATUTS
   * ============================================================
   */

  buildStatuses(): void {

    this.trackingStatuses = [];

    const supplierInvoiceStatuses: InvoiceStatusDefinition[] =
        INVOICE_STATUSES.filter(status => status.supplier);
    for (
      const status of supplierInvoiceStatuses
    ) {

      const curLabel: LabelValue = {

        label:
          this._translate.instant(
            status.code.toString()
          ),

        value:
          status.code,

        otherData:
          null
        };

      this.trackingStatuses.push(
        curLabel
      );
    }
  }

  /*
   * ============================================================
   * AG GRID
   * ============================================================
   */
  
  buildList(): void {

    this.displayedColumns = [
      {
          headerName: '',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: false,
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          sortable: false,
          filter: false,
          resizable: false,
          suppressMenu: true,
          headerClass: 'text-center',
          cellClass: 'text-center'
      },
      {
        field: 'documentType',

        headerName:
          this._translate.instant(
            'documentType'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 150,

        minWidth: 120,

        headerClass:
          'text-center',

        cellClass:
          'text-center',

        valueGetter:
          (params: any): string => {

            return this.getDocumentTypeLabel(
              params.data
            );
          }
      },

      {
        field: 'ref',

        headerName:
          this._translate.instant(
            'supplierInvoiceRef'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 150,

        minWidth: 120,

        headerClass:
          'text-center'
      },

      {
        field: 'supplierName',

        headerName:
          this._translate.instant(
            'supplier'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 220,

        minWidth: 180,

        headerClass:
          'text-center'
      },

      {
        field: 'invoiceDate',

        headerName:
          this._translate.instant(
            'invoiceDate'
          ),
        cellRendererParams: { array: [this.dateFormat]},
        type: 'DateFormat',

        sortable: true,

        filter: true,

        resizable: true,

        width: 130,

        minWidth: 110,

        headerClass:
          'text-center',

        cellClass:
          'text-center'
      },
      {
        field: 'dueDate',

        headerName:
          this._translate.instant(
            'dueDate'
          ),
        cellRendererParams: { array: [this.dateFormat]},
        type: 'DateFormat',

        sortable: true,

        filter: true,

        resizable: true,

        width: 130,

        minWidth: 110,

        headerClass:
          'text-center',

        cellClass:
          'text-center'
      },

      {
        field: 'totalHT',

        headerName:
          this._translate.instant(
            'totalHT'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 120,

        minWidth: 100,

        headerClass:
          'text-center',

        cellClass:
          'text-right',

        valueFormatter:
          (params: any): string => {

            return this.formatAmount(
              params.value
            );
          }
      },

      {
        field: 'totalTVA',

        headerName:
          this._translate.instant(
            'totalTVA'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 120,

        minWidth: 100,

        headerClass:
          'text-center',

        cellClass:
          'text-right',

        valueFormatter:
          (params: any): string => {

            return this.formatAmount(
              params.value
            );
          }
      },

      {
        field: 'totalTTC',

        headerName:
          this._translate.instant(
            'totalTTC'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 120,

        minWidth: 100,

        headerClass:
          'text-center',

        cellClass:
          'text-right',

        valueFormatter:
          (params: any): string => {

            return this.formatAmount(
              params.value
            );
          }
      },

      {
        field: 'currency',

        headerName:
          this._translate.instant(
            'currency'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 90,

        minWidth: 70,

        headerClass:
          'text-center',

        cellClass:
          'text-center'
      },

      {
        field: 'trackingStatus',

        headerName:
          this._translate.instant(
            'trackingStatus'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 220,

        minWidth: 220,

        headerClass:
          'text-center',

        cellClass:
          'text-center',

        valueGetter:
          (params: any): string => {

            return this.getTrackingStatusLabel(
              params.data
            );
          }
      },

      {
        field: 'pdfAvailable',

        headerName:
          this._translate.instant(
            'pdf'
          ),

        sortable: true,

        filter: true,

        resizable: true,

        width: 80,

        minWidth: 70,

        headerClass:
          'text-center',

        cellClass:
          'text-center',

        valueGetter:
          (params: any): string => {

            return params.data &&
              params.data.pdfAvailable === true
              ? this._translate.instant('yes')
              : this._translate.instant('no');
          }
      },

      {
        field: '',

        headerName:
          this._translate.instant(
            'Action'
          ),

        sortable: false,

        filter: false,

        resizable: false,

        width: 180,

        minWidth: 180,

        headerClass:
          'text-center',

        cellRenderer:
          (params: any): string => {

            return this.getActions(
              params.data
            );
          }
      }
    ];


    this.gridOptions = {

        columnDefs:
            this.displayedColumns,

        pagination: true,

        enableFilter: true,

        rowDeselection: true,

        enableColResize: true,

        rowSelection: 'multiple',

        columnTypes: {

            DateFormat: {

                cellRenderer:
                    GridTools.dateRenderer_unixM
            }
        }
    };


    this.defaultColDef = {

      sortable: true,

      resizable: true,

      filter: true
    };


    this.localeText =
      this._translate.getAgGridLabels();
  }


  /*
   * ============================================================
   * LABELS
   * ============================================================
   */

  getDocumentTypeLabel(
    invoice: SupplierInvoiceModel
  ): string {

    if (
      invoice === undefined
      || invoice === null
      || invoice.documentType === undefined
      || invoice.documentType === null
    ) {

      return '';
    }
    const documentType =
      Number(invoice.documentType);

    switch (documentType) {

      case 0:

        return this._translate.instant(
          'invoice'
        );

      case 1:

        return this._translate.instant(
          'advance'
        );

      case 2:

        return this._translate.instant(
          'creditNote'
        );

      default:

        return String(
          invoice.documentType
        );
    }
  }


  getTrackingStatusLabel(
    invoice: SupplierInvoiceModel
  ): string {

    if (
      invoice === undefined
      || invoice === null
      || invoice.trackingStatus === undefined
      || invoice.trackingStatus === null
    ) {

      return '';
    }


    const status =
      this.trackingStatuses.find(
        (item: any) =>
          item.value ===
          Number(invoice.trackingStatus)
      );

    if (
      status === undefined
      || status === null
    ) {

      return invoice.trackingStatus;
    }


    return status.label;
  }


  /*
   * ============================================================
   * FOURNISSEURS
   * ============================================================
   */

  loadSuppliers(): void {

    const user =
      this._userService.getUserLogged();

    if (user === undefined) {
      return;
    }


    this._supplierInvoicesService
      .getSuppliers(user.entity)
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(
        (response: any) => {

          if (
            response === undefined
            || response.body === undefined
            || response.body === null
          ) {

            this.suppliers = [];

            return;
          }


          this.suppliers =
            response.body;


          this.suppliers.sort(
            (
              obj1: SupplierModel,
              obj2: SupplierModel
            ): number => {

              const name1 =
                obj1.name
                  ? obj1.name.toLowerCase()
                  : '';

              const name2 =
                obj2.name
                  ? obj2.name.toLowerCase()
                  : '';


              if (name1 > name2) {
                return 1;
              }

              if (name1 < name2) {
                return -1;
              }

              return 0;
            }
          );
        },

        (error: any) => {

          console.error(
            'Erreur chargement fournisseurs',
            error
          );
        }
      );
  }

  executeSearch(): void {

    this.search();
   
  }
  /*
   * ============================================================
   * RECHERCHE
   * ============================================================
   */

  search(): void {

    const user =
      this._userService.getUserLogged();

    if (user === undefined) {
      return;
    }


    if (
      this.criteria === undefined
      || this.criteria === null
    ) {

      this.criteria =
        new SupplierInvoiceSearchModel();
    }

    this.loadingIndicator = true;

    this.loadingSubject.next(true);


    this.rows = [];

    this.cacheDataTemp = [];


    this._supplierInvoicesService
      .search(
        this._userService
          .getEntityLogged()
          .entity,
        this.criteria
      )
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(

        (response: any) => {

          if (
            response !== undefined
            && response.body !== undefined
            && response.body !== null
          ) {

            this.rows =
              this.affectData(
                response.body
              );

            this.selected = [];

            this.selectedInvoice = null;

            this.invoiceTrackingEvents = [];

          } else {

            this.rows = [];
          }


          this.cacheDataTemp =
            [...this.rows];


          this.loadingIndicator = false;

          this.loadingSubject.next(false);


          if (
            this.gridApi !== undefined
            && this.gridApi !== null
          ) {

            this.gridApi.setRowData(
              this.rows
            );
          }
        },

        (error: any) => {

          this.loadingIndicator = false;

          this.loadingSubject.next(false);

          this.rows = [];

          this.cacheDataTemp = [];


          console.error(
            'Erreur recherche factures fournisseurs',
            error
          );
        }
      );
  }


  /*
   * ============================================================
   * RECHERCHE FORMULAIRE
   * ============================================================
   */

  onSearch(): void {

    if (
      this.criteria === undefined
      || this.criteria === null
    ) {

      this.criteria =
        new SupplierInvoiceSearchModel();
    }


    this.executeSearch();
  }


  resetSearch(): void {

    this.criteria =
      new SupplierInvoiceSearchModel();

    this.executeSearch();
  }


  /*
   * ============================================================
   * FOURNISSEUR
   * ============================================================
   */

  onSupplierChange(
    supplierId: number
  ): void {

    if (
      this.criteria === undefined
      || this.criteria === null
    ) {

      this.criteria =
        new SupplierInvoiceSearchModel();
    }

    this.criteria.supplierId =
      supplierId;


    this.executeSearch();
  }


  /*
   * ============================================================
   * DATES
   * ============================================================
   */

  onDateFromChange(
    date: Date
  ): void {

    if (
      this.criteria === undefined
      || this.criteria === null
    ) {

      this.criteria =
        new SupplierInvoiceSearchModel();
    }

    this.criteria.dateFrom =
      date;


  }


  onDateToChange(
    date: Date
  ): void {

    if (
      this.criteria === undefined
      || this.criteria === null
    ) {

      this.criteria =
        new SupplierInvoiceSearchModel();
    }

    this.criteria.dateTo =
      date;


  }


  /*
   * ============================================================
   * RECHERCHE TEXTE
   * ============================================================
   */

  onSearchTextChange(
    value: string
  ): void {

    if (
      this.criteria === undefined
      || this.criteria === null
    ) {

      this.criteria =
        new SupplierInvoiceSearchModel();
    }

    this.criteria.search =
      value;


    this.executeSearch();
  }


  /*
   * ============================================================
   * ACTIONS
   * ============================================================
   */

  getActions(
    data: SupplierInvoiceModel
  ): string {

    let actions = '';

    /*
    * ============================================================
    * EMPLACEMENT PDF
    * ============================================================
    */

    if (
      data !== undefined
      && data !== null
      && data.pdfAvailable === true
    ) {

      actions +=
        '<button type="button" ' +
        'class="btn btn-default mat-icon-button" ' +
        'data-action-type="pdf" ' +
        'title="' +
        this._translate.instant(
          'displayPdf'
        ) +
        '" ' +
        'style="margin-left:5px;margin-right:5px;">' +

        '<span ' +
        'data-action-type="pdf" ' +
        'class="fas fa-file-pdf" ' +
        'style="font-size:20px">' +
        '</span>' +

        '</button>';

    } else {

      /*
      * Emplacement réservé au PDF.
      * Invisible mais conserve exactement la même largeur.
      */

      actions +=
        '<button type="button" ' +
          'class="btn btn-default mat-icon-button" ' +
          'style="' +
          'margin-left:5px;' +
          'margin-right:5px;' +
          'visibility:hidden;' +
          '">' +
        '</button>';
    }


    /*
    * ============================================================
    * EMPLACEMENT HISTORIQUE
    * ============================================================
    */

    actions +=
      '<button type="button" ' +
      'class="btn btn-default mat-icon-button" ' +
      'data-action-type="history" ' +
      'title="' +
      this._translate.instant(
        'Invoice history'
      ) +
      '" ' +
      'style="margin-left:5px;margin-right:5px;">' +

      '<span ' +
      'data-action-type="history" ' +
      'class="fas fa-history" ' +
      'style="font-size:20px">' +
      '</span>' +

      '</button>';


    /*
    * ============================================================
    * EMPLACEMENT CHANGEMENT DE STATUT
    * ============================================================
    */

    actions +=
      '<button type="button" ' +
      'class="btn btn-default mat-icon-button" ' +
      'data-action-type="changeStatus" ' +
      'title="' +
      this._translate.instant(
        'changeStatus'
      ) +
      '" ' +
      'style="margin-left:5px;margin-right:5px;">' +

      '<span ' +
      'data-action-type="changeStatus" ' +
      'class="fas fa-cog" ' +
      'style="font-size:20px">' +
      '</span>' +

      '</button>';

    return actions;
  }


  /*
   * ============================================================
   * CLIC LIGNE
   * ============================================================
   */

  onRowClicked(
    event: any
  ): void {

    if (
      event === undefined
      || event === null
      || event.event === undefined
      || event.data === undefined
      || event.data === null
    ) {

      return;
    }


    const target =
      event.event.target;


    if (
      target === undefined
      || target === null
    ) {

      return;
    }


    const action =
      target.getAttribute(
        'data-action-type'
      );


    switch (action) {

      case 'pdf':
        this.displayPdf(
          event.data
        );
        break;
      case 'history':

        this.openTrackingHistory(
          event.data
        );

        break;

      case 'changeStatus':

          this.changeStatus(
            event.data
          );

        break;


      default:

        break;
    }
  }

  openTrackingHistory(
    invoice: SupplierInvoiceModel
  ): void {
    if (
      invoice === undefined
      || invoice === null
    ) {

      return;
    }

    this.selectedInvoice =
      invoice;


    this.historyPopupVisible =
      true;
  }
  /*
   * ============================================================
   * PDF
   * ============================================================
   */

  displayPdf(
  invoice: SupplierInvoiceModel
  ): void {

    if (
      invoice === undefined
      || invoice === null
      || invoice.ref === undefined
      || invoice.ref === null
      || invoice.ref.trim() === ''
    ) {

      return;
    }

    const user =
      this._userService.getUserLogged();

    this._supplierInvoicesService
      .getPdf(
        invoice.ref,
        user.entity
      )
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(

        (
          response: HttpResponse<Blob>
        ) => {

          if (
            response === undefined
            || response === null
            || response.body === undefined
            || response.body === null
          ) {

            return;
          }

          const blob =
            new Blob(
              [response.body],
              {
                type: 'application/pdf'
              }
            );

          if (blob.size === 0) {

            return;
          }

          const url =
            window.URL.createObjectURL(
              blob
            );

          this.pdfUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(
              url
            );
          this.pdfPopupVisible = true;
        },

        error => {

          console.log(
            'Erreur affichage PDF',
            error
          );

          console.log(
            'status =',
            error.status
          );

          console.log(
            'statusText =',
            error.statusText
          );

          console.log(
            'error =',
            error.error
          );
        }
      );
  }
  closePdf(): void {

      this.pdfUrl = null;

      this.pdfPopupVisible = false;
  }

  startPdfResize(event: PointerEvent): void {

    event.preventDefault();

    this.pdfResizing = true;

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener(
        'pointermove',
        this.pdfResizeMove
    );

    window.addEventListener(
        'pointerup',
        this.pdfResizeEnd,
        { once: true }
    );
  }


  private pdfResizeMove = (event: PointerEvent): void => {

    if (!this.pdfResizing) {
        return;
    }

    const panel =
        document.querySelector(
            '.supplier-invoice-pdf-panel'
        ) as HTMLElement;

    if (!panel) {
        return;
    }

    let width =
        window.innerWidth - event.clientX;

    const minWidth = 500;

    const maxWidth =
        window.innerWidth - 50;

    width =
        Math.max(
            minWidth,
            Math.min(
                maxWidth,
                width
            )
        );

    panel.style.width =
        `${width}px`;
  }


  private pdfResizeEnd = (): void => {

    this.pdfResizing = false;

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    window.removeEventListener(
        'pointermove',
        this.pdfResizeMove
    );
  }
  /*
   * ============================================================
   * CHANGEMENT STATUT REEL
   * ============================================================
   */

  changeStatus(
    invoice: SupplierInvoiceModel
  ): void {

    if (
      invoice === undefined
      || invoice === null
    ) {

      return;
    }


    this.selectedInvoice =
      invoice;


    const dialogConfig =
      new MatDialogConfig();


    dialogConfig.disableClose =
      true;

    dialogConfig.autoFocus =
      true;

    dialogConfig.width =
      '600px';

    dialogConfig.maxWidth =
      '90vw';
      
    dialogConfig.data = {

      title:
        this._translate.getTranslate(
          'changeInvoiceTrackingStatus'
        ),

      contentMessage:
        invoice.ref,

      typeDialog:
        'changeInvoiceTrackingStatus',

      data1:
        invoice
    };


    const dialogRef =
      this._dialog.open(
        SupplierInvoiceChangeStatusComponent,
        dialogConfig
      );


    dialogRef.afterClosed()
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(
        (data: any) => {

          if (
            data === undefined
            || data === null
          ) {

            return;
          }


          if (
            data.action !== 'okAction'
          ) {

            return;
          }


          if (
            data.trackingStatus === undefined
            || data.trackingStatus === null
            || data.trackingStatus === ''
          ) {

            return;
          }


          this._supplierInvoicesService
          .updateTrackingStatus(
                this._userService
                    .getEntityLogged()
                    .entity,
                invoice.id,
                data.trackingStatus,
                invoice.documentType,
                data.reasonCode,
                data.reason
            )
            .pipe(
              takeUntil(
                this.onDestroy
              )
            )
            .subscribe(

              (response: any) => {

                invoice.trackingStatus =
                  data.trackingStatus;


                if (
                  this.gridApi !== undefined
                  && this.gridApi !== null
                ) {

                  this.gridApi.refreshCells({
                    force: true
                  });
                }
              },

              (error: any) => {

                console.error(
                  'Erreur mise à jour statut facture électronique',
                  error
                );
              }
            );
        }
      );
  }
  /*
   * ============================================================
   * TRACKING REEL
   * ============================================================
   */

  loadTrackingEvents(
    invoice: SupplierInvoiceModel
  ): void {

    if (
      invoice === undefined
      || invoice === null
      || invoice.id === undefined
      || invoice.id === null
    ) {

      this.invoiceTrackingEvents = [];

      return;
    }


    this.invoiceTrackingEvents = [];


    this._supplierInvoicesService
      .getTrackingEvents(
        this._userService
          .getEntityLogged()
          .entity,

        String(invoice.id)
      )
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(

        (
          response:
            HttpResponse<
              InvoiceTrackingEventModel[]
            >
        ) => {

          if (
            response === undefined
            || response === null
            || response.body === undefined
            || response.body === null
          ) {

            this.invoiceTrackingEvents = [];

            return;
          }


          this.invoiceTrackingEvents =
            response.body;
        },

        (error: any) => {

          this.invoiceTrackingEvents = [];

          console.error(
            'Erreur récupération historique facture fournisseur',
            error
          );
        }
      );
  }


  /*
   * ============================================================
   * SELECTION
   * ============================================================
   */

    onSelectionChanged(): void {

      if (
          this.gridApi === undefined
          || this.gridApi === null
      ) {

          return;
      }

      const selectedRows =
          this.gridApi.getSelectedRows();

      if (
          selectedRows === undefined
          || selectedRows === null
          || selectedRows.length === 0
      ) {

          this.selected = [];

          this.selectedInvoice = null;

          this.invoiceTrackingEvents = [];

          return;
      }

      this.selected =
          selectedRows;

      if (
          selectedRows.length === 1
      ) {

          this.selectedInvoice =
              selectedRows[0];

          this.loadTrackingEvents(
              this.selectedInvoice
          );

      } else {

          this.selectedInvoice = null;

          this.invoiceTrackingEvents = [];
      }
  }
  getSelectedInvoiceCount(): number {

      if (
          this.selected === undefined
          || this.selected === null
      ) {

          return 0;
      }

      return this.selected.length;
  }

  /*
   * ============================================================
   * FILTRE AG GRID
   * ============================================================
   */

  onFilterTextBoxChanged(
    event: any
  ): void {

    if (
      this.gridApi === undefined
      || this.gridApi === null
    ) {

      return;
    }


    let valueFilter = '';


    if (
      event !== undefined
      && event !== null
      && event.target !== undefined
      && event.target !== null
    ) {

      valueFilter =
        event.target.value;
    }


    this.gridApi.setQuickFilter(
      valueFilter
    );
  }


  /*
   * ============================================================
   * GRID READY
   * ============================================================
   */

  onGridReady(
    params: any
  ): void {

    if (
      params === undefined
      || params === null
    ) {

      return;
    }


    this.gridApi =
      params.api;

    this.gridColumnApi =
      params.columnApi;


    if (
      this.rows !== undefined
      && this.rows !== null
    ) {

      this.gridApi.setRowData(
        this.rows
      );
    }


    this.autoSizeAll(
      false
    );

  }


  /*
   * ============================================================
   * AUTOSIZE
   * ============================================================
   */

  autoSizeAll(
    skipHeader: boolean
  ): void {

    if (
      this.gridColumnApi === undefined
      || this.gridColumnApi === null
    ) {

      return;
    }


    const allColumnIds: string[] = [];


    const columns =
      this.gridColumnApi.getAllColumns();


    if (
      columns === undefined
      || columns === null
    ) {

      return;
    }


    columns.forEach(
      (column: any) => {

        if (
          column !== undefined
          && column !== null
        ) {

          allColumnIds.push(
            column.colId
          );
        }
      }
    );


    if (
      allColumnIds.length > 0
    ) {

      this.gridColumnApi
        .autoSizeColumns(
          allColumnIds,
          skipHeader
        );
    }
  }


  /*
   * ============================================================
   * DONNEES
   * ============================================================
   */

  affectData(
    dataArray: SupplierInvoiceModel[]
  ): SupplierInvoiceModel[] {

    if (
      dataArray === undefined
      || dataArray === null
    ) {

      return [];
    }


    for (
      const itemCur of dataArray
    ) {

      if (
        itemCur.pdfAvailable === undefined
        || itemCur.pdfAvailable === null
      ) {

        itemCur.pdfAvailable =
          false;
      }
    }


    return dataArray;
  }


  /*
   * ============================================================
   * MONTANTS
   * ============================================================
   */

  formatAmount(
    value: any
  ): string {

    if (
      value === undefined
      || value === null
      || value === ''
    ) {

      return '';
    }


    const numberValue =
      Number(value);


    if (
      isNaN(numberValue)
    ) {

      return '';
    }


    return numberValue.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );
  }


  /*
   * ============================================================
   * REFRESH
   * ============================================================
   */

  refresh(): void {

    this.loadingIndicator =
      true;

    this.buildList();

    this.search();
  }


  /*
   * ============================================================
   * TRADUCTIONS
   * ============================================================
   */

  translateLabels(): void {

    this.buildStatuses();

    this.buildList();


    if (
      this.gridApi !== undefined
      && this.gridApi !== null
    ) {

      this.gridApi.setColumnDefs(
        this.displayedColumns
      );

      this.localeText =
        this._translate.getAgGridLabels();

      this.gridApi.refreshHeader();

      this.autoSizeAll(
        false
      );
    }
  }
  /*
   * ============================================================
   * IMPRESSION
   * ============================================================
   */
  printSelectedInvoices(): void {

    if (this.selected === undefined
        || this.selected === null
        || this.selected.length === 0
    ) {
        return;
    }

    const invoiceCount =
        this.selected.length;

    const message =
        this._translate.instant(
            'confirmPrintSupplierInvoices'
        )
        + ' '
        + invoiceCount
        + ' '
        + this._translate.instant(
            'supplierInvoices'
        )
        + ' ?';

    const dialogConfig =
        new MatDialogConfig();

    dialogConfig.disableClose =
        true;

    dialogConfig.autoFocus =
        true;

    dialogConfig.data = {

        id: 1,

        title:
            this._translate.instant(
                'printSupplierInvoices'
            ),

        typeDialog:
            'confirmAction',

        panelClass:
            'stdTheme',

        contentMessage:
            message,

        data1:
            invoiceCount,

        data2:
            ''
    };

    const dialogRef =
        this._dialog.open(
            ModalCommonComponent,
            dialogConfig
        );

    dialogRef.afterClosed()
        .pipe(
            takeUntil(
                this.onDestroy
            )
        )
        .subscribe(
            (data: any) => {

                if (
                    data === undefined
                    || data === null
                ) {

                    return;
                }

                if (
                    data === 'okAction'
                ) {

                    this.executePrintSelectedInvoices();
                }
            }
        );
  }
  executePrintSelectedInvoices(): void {

    if (
      this.selected === undefined
      || this.selected === null
      || this.selected.length === 0
    ) {
      return;
    }

    const user =
      this._userService.getUserLogged();

    if (user === undefined) {
      return;
    }

    const invoiceIds: string[] =
      this.selected
        .map(
          (invoice: SupplierInvoiceModel) =>
            String(invoice.id)
        );

    this.waitingPrint = true;

    this._supplierInvoicesService
      .printSuppiersInvoices(
        invoiceIds,
        user.entity
      )
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(
        (response: HttpResponse<Blob>) => {

          this.waitingPrint = false;

          const blob =
            response.body;

          if (
            blob === undefined
            || blob === null
            || blob.size === 0
          ) {

            console.error(
              'Le PDF retourné est vide'
            );

            return;
          }

          const url =
            window.URL.createObjectURL(
              blob
            );

          window.open(
            url,
            '_blank'
          );

          setTimeout(
            () => {

              window.URL.revokeObjectURL(
                url
              );

            },
            1000
          );
        },

        (error: any) => {

          this.waitingPrint = false;

          console.error(
            'Erreur lors de l\'impression des factures fournisseurs',
            error
          );
        }
      );
  }
  /*
   * ============================================================
   * EVENEMENTS GLOBAUX
   * ============================================================
   */

  subscribeAll(): void {

    this._globalEventsManagerService
      .synchroEvents
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(
        (event: any) => {

          if (
            event === undefined
            || event === null
          ) {

            return;
          }


          if (
            typeof event === 'string'
          ) {

            const eventString =
              String(event);


            if (
              eventString ===
              'refreshSupplierInvoices'
            ) {
              this.search();
            }
          }
        }
      );
  }


  /*
   * ============================================================
   * NOTIFICATIONS
   * ============================================================
   */

  connectNotifications(): void {

    const user =
      this._userService.getUserLogged();


    if (
      user === undefined
    ) {

      return;
    }


    this.notificationsSubscription =
      this._notificationsService
        .connectNotif(
          user.usergroup
        )
        .pipe(
          takeUntil(
            this.onDestroy
          )
        )
        .subscribe(

          (notif: any) => {

            console.log(
              'Notification facture fournisseur :',
              notif
            );
          },

          (error: any) => {

            console.error(
              'Erreur notifications factures fournisseurs',
              error
            );
          }
        );
  }


  /*
   * ============================================================
   * MESSAGES
   * ============================================================
   */

  displayMessageBox(
    titleBox: string,
    messageBox: string,
    messageType: string,
    typeDialog: string,
    actionCur: string,
    dataArg: any
  ): void {

    const dialogConfig =
      new MatDialogConfig();


    dialogConfig.disableClose =
      true;

    dialogConfig.autoFocus =
      true;


    dialogConfig.data = {

      id: 1,

      title:
        titleBox,

      typeDialog:
        typeDialog,

      panelClass:
        'stdTheme',

      contentMessage:
        messageBox,

      data1:
        '',

      data2:
        '',

      messageType:
        messageType
    };


    const dialogRef =
      this._dialog.open(
        ModalCommonComponent,
        dialogConfig
      );


    dialogRef.afterClosed()
      .pipe(
        takeUntil(
          this.onDestroy
        )
      )
      .subscribe(
        (data: any) => {

          if (
            data === undefined
            || data === null
          ) {

            return;
          }


          if (
            data === 'okAction'
            && actionCur ===
              'readingSupplierInvoices'
          ) {

            this.loadingSubject.next(
              false
            );

            this.loadingIndicator =
              false;
          }
        }
      );
  }

  getSearchDateString(
    value: any
  ): string {

    if (
      value === undefined
      || value === null
      || value === ''
    ) {

      return null;
    }


    /*
    * Date JavaScript
    */

    if (
      value instanceof Date
    ) {

      const year =
        value.getFullYear();

      const month =
        String(
          value.getMonth() + 1
        ).padStart(
          2,
          '0'
        );

      const day =
        String(
          value.getDate()
        ).padStart(
          2,
          '0'
        );


      return (
        year +
        '-' +
        month +
        '-' +
        day
      );
    }


    /*
    * Moment
    */

    if (
      typeof value.format === 'function'
    ) {

      return value.format(
        'YYYY-MM-DD'
      );
    }


    /*
    * Chaîne
    *
    * 2026-08-01 00:00:00
    */

    const text =
      String(value);


    if (
      text.length >= 10
      && text.charAt(4) === '-'
      && text.charAt(7) === '-'
    ) {

      return text.substring(
        0,
        10
      );
    }


    return null;
  }
  getTestTimestamp(
    value: any
  ): number {

    if (
      value === undefined
      || value === null
      || value === ''
    ) {

      return null;
    }


    /*
    * ============================================================
    * Moment
    * ============================================================
  */

    if (
      typeof value.toDate === 'function'
    ) {

      const date =
        value.toDate();


      return date.getTime();
    }


    /*
    * ============================================================
    * Date JavaScript
    * ============================================================
  */

    if (
      value instanceof Date
    ) {

      if (
        isNaN(
          value.getTime()
        )
      ) {

        return null;
      }


      return value.getTime();
    }


    /*
    * ============================================================
    * CHAINE
    * ============================================================
  */

    const text =
      String(value).trim();


    /*
    * Format :
    *
    * 2026-08-01 00:00:00
    *
    * ou
    *
    * 2026-08-01
    */

    let match =
      text.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/
      );


    if (
      match !== null
    ) {

      const year =
        Number(match[1]);

      const month =
        Number(match[2]);

      const day =
        Number(match[3]);

      const hour =
        match[4] !== undefined
          ? Number(match[4])
          : 0;

      const minute =
        match[5] !== undefined
          ? Number(match[5])
          : 0;

      const second =
        match[6] !== undefined
          ? Number(match[6])
          : 0;


      return new Date(
        year,
        month - 1,
        day,
        hour,
        minute,
        second,
        0
      ).getTime();
    }


    /*
    * ============================================================
    * Format :
    *
    * dd/MM/yyyy
    * ============================================================
  */

    match =
      text.match(
        /^(\d{2})\/(\d{2})\/(\d{4})$/
      );


    if (
      match !== null
    ) {

      return new Date(
        Number(match[3]),
        Number(match[2]) - 1,
        Number(match[1]),
        0,
        0,
        0,
        0
      ).getTime();
    }


    return null;
  }
  getTestDateValue(
    value: any
  ): number {

    if (
      value === undefined
      || value === null
      || value === ''
    ) {

      return null;
    }


    /*
    * ============================================================
    * MOMENT
    * ============================================================
  */

    if (
      typeof value.toDate === 'function'
    ) {

      const date =
        value.toDate();


      return (
        date.getFullYear() * 10000000000
        +
        (date.getMonth() + 1) * 100000000
        +
        date.getDate() * 1000000
        +
        date.getHours() * 10000
        +
        date.getMinutes() * 100
        +
        date.getSeconds()
      );
    }


    /*
    * ============================================================
    * DATE JAVASCRIPT
    * ============================================================
  */

    if (
      value instanceof Date
    ) {

      return (
        value.getFullYear() * 10000000000
        +
        (value.getMonth() + 1) * 100000000
        +
        value.getDate() * 1000000
        +
        value.getHours() * 10000
        +
        value.getMinutes() * 100
        +
        value.getSeconds()
      );
    }


    const text =
      String(value).trim();


    /*
    * ============================================================
    * yyyy-MM-dd HH:mm:ss
    * ============================================================
  */

    let match =
      text.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/
      );


    if (
      match !== null
    ) {

      return (
        Number(match[1]) * 10000000000
        +
        Number(match[2]) * 100000000
        +
        Number(match[3]) * 1000000
        +
        Number(match[4] || '0') * 10000
        +
        Number(match[5] || '0') * 100
        +
        Number(match[6] || '0')
      );
    }


    /*
    * ============================================================
    * dd/MM/yyyy
    * ============================================================
  */

    match =
      text.match(
        /^(\d{2})\/(\d{2})\/(\d{4})$/
      );


    if (
      match !== null
    ) {

      return (
        Number(match[3]) * 10000000000
        +
        Number(match[2]) * 100000000
        +
        Number(match[1]) * 1000000
      );
    }


    return null;
  }
  normalizeTestSearchDate(
    value: any,
    endOfDay: boolean
  ): Date {

    if (
      value === undefined
      || value === null
      || value === ''
    ) {

      return null;
    }


    /*
    * ============================================================
    * CAS Date Angular Material
    * ============================================================
    */

    if (
      value instanceof Date
    ) {

      const result =
        new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate()
        );


      if (endOfDay) {

        result.setHours(
          23,
          59,
          59,
          999
        );

      } else {

        result.setHours(
          0,
          0,
          0,
          0
        );
      }


      return result;
    }


    const text =
      String(value).trim();


    /*
    * ============================================================
    * yyyy-MM-dd HH:mm:ss
    * ============================================================
    */

    let match =
      text.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/
      );


    if (
      match !== null
    ) {

      const result =
        new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
          match[4]
            ? Number(match[4])
            : 0,
          match[5]
            ? Number(match[5])
            : 0,
          match[6]
            ? Number(match[6])
            : 0
        );


      if (endOfDay) {

        result.setHours(
          23,
          59,
          59,
          999
        );
      }


      return result;
    }


    /*
    * ============================================================
    * dd/MM/yyyy
    * ============================================================
    */

    match =
      text.match(
        /^(\d{2})\/(\d{2})\/(\d{4})$/
      );


    if (
      match !== null
    ) {

      const result =
        new Date(
          Number(match[3]),
          Number(match[2]) - 1,
          Number(match[1])
        );


      if (endOfDay) {

        result.setHours(
          23,
          59,
          59,
          999
        );

      } else {

        result.setHours(
          0,
          0,
          0,
          0
        );
      }


      return result;
    }


    return null;
  }
  parseTestDate(
    value: any
  ): Date {

    if (
      value === undefined
      || value === null
    ) {

      return null;
    }


    if (
      value instanceof Date
    ) {

      return new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds()
      );
    }


    const valueString =
      String(value).trim();


    /*
    * Format des données de test :
    *
    * 2026-08-01 00:00:00
    *
    * ou
    *
    * 2026-08-01
    */

    const match =
      valueString.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/
      );


    if (
      match !== null
    ) {

      return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        match[4]
          ? Number(match[4])
          : 0,
        match[5]
          ? Number(match[5])
          : 0,
        match[6]
          ? Number(match[6])
          : 0
      );
    }


    return null;
  }
  
  closeTrackingHistory(): void {

    this.historyPopupVisible =
      false;
  }
  
  /*
   * ============================================================
   * DESTRUCTION
   * ============================================================
   */

  ngOnDestroy(): void {

    if (
      this.notificationsSubscription !== undefined
      && this.notificationsSubscription !== null
    ) {

      this.notificationsSubscription.unsubscribe();
    }


    this.onDestroy.next();

    this.onDestroy.complete();
  }
}
